import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { type UseReactToPrintOptions } from 'react-to-print';

import { ReactComponent as LogoIcon } from '@assets/svg/logo-icon.svg';
import { ReactComponent as LogoNameIcon } from '@assets/svg/logo-name.svg';

import Header from '@components/common/Header/Header';
import PrintIconBtn from '@components/common/Header/components/BtnHeaderPrint/BtnHeaderPrint';

import useTranslation from '@hooks/useTranslation';

import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import ActionItemsStatuses from './components/ActionItemsStatuses/ActionItemsStatuses';
import AssetHealthOverview from './components/AssetHealthOverview/AssetHealthOverview';
import SiteEngagementMetrics from './components/SiteEngagementMetrics/SiteEngagementMetrics';
import TopFailureCauseCategories from './components/TopFailureCauseCategories/TopFailureCauseCategories';
import TotalCostSaving from './components/TotalCostSaving/TotalCostSaving';
import style from './style.module.scss';

const ExecutiveSummary = () => {
  const { trackPageView } = useMatomo();
  const { customerId } = useParams();
  const locationSelectValue = selectAppLocationSelectValue();

  const { t } = useTranslation();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const printRef = useRef<HTMLInputElement>(null);
  const [isPrint, setIsPrint] = useState(false);

  useEffect(() => {
    trackPageView({});
  }, []);

  const handleOnBeforeGetContent = () => {
    setIsPrint(true);
    return new Promise<void>((resolve) => setTimeout(resolve));
  };

  const handleAfterPrint = () => {
    setIsPrint(false);
  };

  const printParams: UseReactToPrintOptions = {
    onBeforePrint: handleOnBeforeGetContent,
    onAfterPrint: handleAfterPrint,
  };

  return (
    <div ref={printRef}>
      <Header
        title={t('Executive Summary')}
        showLogo={true}
        showFacilitySelect={true}
        headerRightComponent={
          <PrintIconBtn printRef={printRef} printParams={printParams} isLoading={isPrint} />
        }
      />
      <div className={style.printHeader}>
        <LogoIcon className={style.logoIcon} />
        <LogoNameIcon className={style.logoNameIcon} />
      </div>
      <div className={style.executiveSummary}>
        <AssetHealthOverview />
        {customerInfoData?.showTotalCostSavings && <TotalCostSaving />}
        <ActionItemsStatuses />
        <TopFailureCauseCategories />
        <SiteEngagementMetrics />
      </div>
    </div>
  );
};

export default ExecutiveSummary;
